import { Component, Mixins, Prop } from 'vue-property-decorator'
import { KTable } from '@kwixeo/table-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import Table from '@kwixeo/table-vue/dist/components/Table/Table'
import { emitter, NumberMixin, Spinner } from '@kwixeo/interface-vue'
import LotDelete from '../LotDelete/LotDelete.vue'
import LotItem from '../LotItem/LotItem.vue'

@Component({
  methods: {
    ...mapActions('dynamicModals', ['openModal']),
    ...mapActions('catalog', ['loadStockLocations'])
  },
  computed: {
    ...mapGetters('subscriptions', ['hasModule']),
    ...mapState('catalog', ['stockLocations'])
  },
  components: {
    KTable,
    LotItem,
    LotDelete,
    Spinner
  }
})
export default class LotList extends Mixins(NumberMixin) {
  openModal!: (params: string|KwixeoInterface.OpenModalParamObject) => void
  hasModule!: (key: string) => boolean
  loadStockLocations!: (force?: boolean) => Promise<void>
  stockLocations!: KwixeoCatalog.StockLocations

  @Prop({ default: null }) linkedElement!: null|{ type: string; id: number; }
  @Prop({ default: null }) productId!: null|number

  private loading = false
  private showLot: KwixeoCatalog.ProductLot|null = null
  private deleteLot: KwixeoCatalog.ProductLot|null = null

  async mounted (): Promise<void> {
    if (this.hasModule('stock-locations')) {
      this.loading = true
      await this.loadStockLocations()
      this.loading = false
    }

    emitter.on('productLotUpdated', this.lotUpdated)
    emitter.on('productLotDeleted', this.lotDeleted)
  }

  destroyed (): void {
    emitter.off('productLotUpdated', this.lotUpdated)
    emitter.off('productLotDeleted', this.lotDeleted)
  }

  get stateKey (): string {
    return 'stock-lots' + (this.productId ? '-product' : '')
  }

  get rowActions (): KwixeoTable.RowActions {
    return [
      {
        icon: 'edit',
        click: this.toEdit
      },
      {
        icon: 'delete',
        click: this.toDelete
      }
    ]
  }

  get columns (): KwixeoTable.Columns {
    const columns: KwixeoTable.Columns = []

    if (!this.productId) {
      columns.push(...[
        {
          data: 'product.reference',
          name: this.$t('catalog.productLot.list.productReference')
        },
        {
          data: 'product.name',
          name: this.$t('catalog.productLot.list.productName')
        }
      ])
    }

    if (this.hasModule('stock-locations')) {
      columns.push({
        data: 'stock_location.name',
        name: 'Lieu de stock'
      })
    }

    columns.push(...[
      {
        data: 'reference',
        name: this.$t('catalog.productLot.list.reference')
      },
      {
        data: 'expire_at',
        name: this.$t('catalog.productLot.list.expire'),
        render: 'date'
      },
      {
        data: 'quantity',
        name: this.$t('catalog.productLot.list.quantity'),
        render: (val: number) => this.showNumber(val),
        cssClass: 'kw-table-render-number'
      },
      {
        data: 'stock_quantity',
        name: this.$t('catalog.productLot.list.stockQuantity'),
        render: (val: number) => this.showNumber(val),
        cssClass: 'kw-table-render-number'
      },
      {
        data: 'created_at',
        name: this.$t('interface.list.createdAt'),
        visible: false,
        render: 'datetime'
      },
      {
        data: 'updated_at',
        name: this.$t('interface.list.updatedAt'),
        visible: false,
        render: 'datetime'
      }
    ])

    return columns
  }

  get ajaxDatas (): KwixeoTable.Object {
    const filter: KwixeoTable.Object = {}
    if (this.linkedElement) {
      filter.element = this.linkedElement
    }
    if (this.productId) {
      filter.where_product = this.productId
    }

    return {
      include: ['product', 'stock_location'],
      filter
    }
  }

  get filters (): KwixeoTable.FiltersOption {
    const filters: KwixeoTable.FiltersOption = []

    if (!this.productId) {
      filters.push({
        type: 'text',
        label: this.$t('catalog.productLot.filter.productReference'),
        name: 'product_reference'
      })
    }

    if (this.hasModule('stock-locations')) {
      filters.push({
        type: 'select',
        name: 'stock_location',
        label: 'Lieu de stock',
        options: this.stockLocations,
        optionLabel: 'name',
        value: 'id',
        multiple: true
      })
    }

    filters.push(...[
      {
        type: 'text',
        label: this.$t('catalog.productLot.filter.reference'),
        name: 'reference'
      },
      {
        type: 'select',
        label: this.$t('catalog.productLot.filter.inStock'),
        name: 'only_in_stock',
        options: [
          { value: true, label: 'Oui' },
          { value: false, label: 'Non' }
        ],
        optionLabel: 'label',
        value: 'value'
      },
      {
        type: 'period',
        label: this.$t('catalog.productLot.filter.expire'),
        name: 'expire_since',
        name2: 'expire_until'
      }
    ] as KwixeoTable.FiltersOption)

    return filters
  }

  private rowClick (row: KwixeoCatalog.ProductLot): void {
    this.showLot = row
  }

  private toEdit (lot: KwixeoCatalog.ProductLot): void {
    this.openModal({
      key: 'catalog-product-lot',
      value: lot
    })
  }

  private lotUpdated (lot: KwixeoCatalog.ProductLot): void {
    const table = this.$refs.table as Table
    const datas = table.getDatas()
    const index = datas.findIndex(row => row.id === lot.id)
    if (index >= 0) {
      const old = datas[index]
      table.updateRow(index, lot)
      this.$emit('lotUpdated', lot, old)
    } else {
      table.addRow(lot)
      this.$emit('lotCreated', lot)
    }
  }

  private toDelete (lot: KwixeoCatalog.ProductLot): void {
    this.deleteLot = lot
  }

  private lotDeleted (lot: KwixeoCatalog.ProductLot): void {
    const table = this.$refs.table as Table
    const index = table.getDatas().findIndex(row => row.id === lot.id)
    if (index >= 0) {
      table.deleteRow(index)
      this.$emit('lotDeleted', lot)
    }
  }

  refresh (): void {
    const table = this.$refs.table as Table
    table.refresh()
  }
}
